import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@tytapp/user';
import { SubSink } from 'subsink';
import { AppConfig, Redirection, Cookies, Transfer, Page, PageComponent } from '@tytapp/common';
import { ApiOffer, ApiOfferSet, ApiProduction, ApiUser, ApiVOD, OffersApi, OfferSetsApi, ProductionsApi, ShoutOutsApi } from '@tytapp/api';
import { environment } from 'environments/environment';
import { v4 as uuid } from 'uuid';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { isClientSide } from '../../environment-utils';

@Page('confirmed-offers')
@Component({
  selector: 'tyt-confirmed-offers',
  templateUrl: './confirmed-offers.component.html',
  styleUrls: ['./confirmed-offers.component.scss']
})
export class ConfirmedOffersComponent extends PageComponent {

  backgroundImage = '/assets/join-path/modern_company_value_bg.jpg';

  private shoutOutsApi = inject(ShoutOutsApi);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private offerSetsApi = inject(OfferSetsApi);
  private productionsApi = inject(ProductionsApi);
  private cookies = inject(Cookies);
  private recaptchaV3Service = inject(ReCaptchaV3Service);

  @Transfer()
  impressionUuid: string = null;

  recaptchaToken: string = null;

  subsink = new SubSink();
  user : ApiUser;

  goUrl: string;
  goText = 'Go to TYT.com';
  enjoyLegend = '';

  pixelImpression = false;

  get displayedGoText() {
    if (this.shoutOutPref) {
      return `Save and ${this.goText}`;
    }
    // If a landing page is set, always display "Continue"
    if (this.hasLandingPageUrl()) {
        return 'Continue';
    }
    return this.goText;
  }

  get isFramed() {
    if (typeof window === 'undefined')
      return false;
    return window.top !== window.self;
  }

  get pixelEndpoint() {
    return `${this.offerSetUUID}:${this.offerUUID}/confirmed`;
  }

  get pixelUrl() {
    let url = `${environment.urls.api}/membership/offer_sets/${this.pixelEndpoint}/impression`;
    const params: string[] = [];
    if (this.impressionUuid) {
      params.push(`uuid=${this.impressionUuid}`);
    }
    if (this.recaptchaToken) {
      params.push(`au=${this.recaptchaToken}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return url;
  }

  get liveScheduleUrl() {
    return `${environment.urls.app}/live`;
  }

  private offerSetUUID : string;
  private offerUUID : string;

  shoutOutPref: string;
  acceptedOffer: ApiOffer;
  presentedOfferSet: ApiOfferSet;

  thankYouMessage = 'Thank You & Welcome to <span style="color:#EFB42C;">PLUS</span>';

  plusMember = true;

  purchaseType: 'Contribution' | 'Membership';
  purchaseUUID: string;
  contributionGrant: number = 0;

  unitLabel(value: number, singularUnit: string, pluralUnit: string) {
    if (value === 1)
      return singularUnit;

    return pluralUnit;
  }

  init() {
    this.impressionUuid ??= uuid();
    if (isClientSide()) {
      this.recaptchaV3Service.execute('offer_set_impression').subscribe(
        token => {
          this.recaptchaToken = token;
        },
        error => {
          console.error('reCaptcha check failed:', error);
        }
      );
    }

    this.goUrl = environment.urls.app;

    this.subsink.add(this.userService.userChanged.subscribe(user => this.user = user));

    this.subsink.add(this.route.queryParamMap.subscribe(async params => {
      this.purchaseType = <any>params.get('pt');
      this.purchaseUUID = params.get('pu');

      // Set the tyt_last_purchase cookie
      if (this.purchaseType && this.purchaseUUID) {
          this.cookies.set('tyt_last_purchase', `${this.purchaseType}:${this.purchaseUUID}`, null);
      }

      if(this.purchaseType === 'Contribution') {
        this.thankYouMessage = 'Thank you for your <span style="color:#EFB42C">contribution!</span>';

        this.contributionGrant = Number(params.get('cg') || '0');
        this.plusMember = this.contributionGrant > 0;
      }
    }));

    let isFirstParamChange = true;
    this.subsink.add(this.route.paramMap.subscribe(async params => {
      if (isFirstParamChange) {
        isFirstParamChange = false;
      } else {
        this.impressionUuid = null;
        this.recaptchaToken = null;
      }

      const offerSetUUID = params.get('id');
      const offerSet = await this.offerSetsApi.get(offerSetUUID, false).toPromise();

      this.offerSetUUID = offerSetUUID;
      this.offerUUID = params.get('offerId');

      this.presentedOfferSet = offerSet;
      this.acceptedOffer = offerSet.offers.find(x => x.uuid === this.offerUUID);

      this.embedOfferSnippet();

      this.pixelImpression = true;

      // --- New Landing Page URL Logic ---
      let landingPageUrl: string;

      if (this.acceptedOffer?.landing_page_url) {
        landingPageUrl = this.acceptedOffer.landing_page_url;
      } else if (this.presentedOfferSet?.landing_page_url) {
        landingPageUrl = this.presentedOfferSet.landing_page_url;
      }

      if (landingPageUrl) {
          this.goUrl = landingPageUrl;
          this.goText = 'Continue'; // This will be overridden by displayedGoText getter
      } else if(offerSet.featured_media_production_cfid) {
        let production : ApiProduction;

        try {
          production = await this.productionsApi.get(offerSet.featured_media_production_cfid).toPromise();
        } catch (e) {
          console.log('Not able to load the featured media. Reason:');
          console.error(e);
        }

        if (production) {
          for(let episode of production.full_length_vods) {
            (<any><Partial<ApiVOD>> episode).type = 'episodes';
          }

          for(let vod of production.vod_clips) {
            (<any><Partial<ApiVOD>> vod).type = 'clips';
          }

          const vodList = [...production.full_length_vods, ...production.vod_clips];

          for(let vod of vodList) {
            if(vod.id === offerSet.featured_media_vod_cfid && vod.is_premium) {
              this.goText = 'Go to Video';
              this.goUrl = `${environment.urls.app}/watch/${production.id}/${(<any> vod).type}/${vod.id}`;
              this.enjoyLegend = 'Continue to Enjoy the Full <span style="color:#EFB42C">PLUS</span> version of the video you were just watching';
            }
          }
        }
      } else if(offerSet.featured_article_cfid) {
        this.goText = 'Go to Article';
        this.goUrl = `${environment.urls.app}/stories/${offerSet.featured_article_cfid}`;
        this.enjoyLegend = 'Continue to Enjoy the Full <span style="color:#EFB42C">PLUS</span> version of the article you were just reading';
      }

      this.state = 'ready';
    }));
  }

  destroy() {
    this.subsink.unsubscribe();
  }

  state: 'loading' | 'ready' = 'loading';
  loadingMessage = 'Just a moment...';

  async saveAndProceed() {
    this.state = 'loading';

    // Only attempt shout out logic if there is no landing page URL
    if (!this.hasLandingPageUrl()) {
        if (this.shoutOutPref === 'yes') {
            try {
                await this.shoutOutsApi.requestShoutOut({
                    purchase_type: this.purchaseType,
                    purchase_uuid: this.purchaseUUID,
                    allow_shout_out: true
                }).toPromise();
            } catch (e) {
                console.error(`Caught error while requesting shout out:`);
                console.error(e);

                let errorMessage = e.message ?? e.error ?? 'An unknown error has occurred';

                if (e.error === 'not-found') {
                    errorMessage = `This feature is not currently available.`;
                } else if (e.error === 'unauthenticated') {
                    this.shell.alert(
                        `Uh oh`,
                        `
                            We couldn't submit your shout out request because you've been signed out. Please sign in on TYT.com
                            and try again.
                        `
                    );
                    this.state = 'ready';
                    return;
                }

                await new Promise(r => setTimeout(r, 1000));
                this.shell.alert(
                    `Uh oh`,
                    `
                        There was a problem requesting your shout out:
                        <br/><br/>
                        ${errorMessage}
                        <br/><br/>
                        Please try again. If the issue persists, let us know at support@tytnetwork.com!
                    `
                );

                this.state = 'ready';
                return;
            }
        } else if (this.shoutOutPref === 'no') {
            try {
                await this.shoutOutsApi.requestShoutOut({
                    purchase_type: this.purchaseType,
                    purchase_uuid: this.purchaseUUID,
                    allow_shout_out: false
                }).toPromise();
            } catch (e) {
                console.log('There was an error when shoutOutPref was set to no', e);
            }
        }
    }


    // Make sure to use window.open() here because sometimes we are in an embedded
    // offer set (iframe) and we don't want to open the destination URL within that
    // iframe.

    window.open(this.goUrl, "_top");
  }

  @ViewChild('offerSnippet')
  offerSnippetElem: ElementRef;

  async embedOfferSnippet() {
    if (!this.acceptedOffer)
      return;

    if (this.acceptedOffer.confirmation_page_javascript) {
      this.offerSnippetElem.nativeElement.appendChild(
        document.createRange()
          .createContextualFragment(this.acceptedOffer.confirmation_page_javascript)
      );
    }
  }

  get enablePixelDebugger() {
    return this.shell.hasFeatureSync('apps.accounts.enable_pixel_debugger');
  }

  hasLandingPageUrl(): boolean {
    return !!(this.acceptedOffer?.landing_page_url || this.presentedOfferSet?.landing_page_url);
  }
}
